import React from 'react';
import countBy from 'lodash/countBy';
import { Box, Dialog, DialogContent, Fade, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

import { ScoreBucketKey, ScoreDistribution, HoleRow, CourseRow, bucketOf } from '../data/hole_insights';
import { DS, Overline, formatSigned, numericSx, trendColor } from './Dashboard';

export const BUCKET_COLORS: Record<ScoreBucketKey, string> = {
    ace: DS.gold,
    eagle: DS.info,
    birdie: DS.gain,
    par: DS.neutral,
    bogey: '#FF8A65',
    double: DS.loss,
};

export const formatToPar = (value: number, digits = 1) =>
    Number(value.toFixed(digits)) === 0 ? 'E' : formatSigned(value, digits);

/*****************************************************************************
 ** Segmented distribution of scores
*****************************************************************************/

export const DistributionBar: React.FC<{ distribution: ScoreDistribution; height?: number }> = ({ distribution, height = 14 }) => (
    <Box sx={{ display: 'flex', height, gap: '2px', borderRadius: '999px', overflow: 'hidden', background: DS.inset }}>
        {distribution.buckets.filter(b => b.count > 0).map(b => (
            <Tooltip key={b.key} arrow title={`${b.label}: ${b.count} (${b.percent}%)`}>
                <Box sx={{ width: `${b.percent}%`, minWidth: 3, background: BUCKET_COLORS[b.key] }} />
            </Tooltip>
        ))}
    </Box>
);

/*****************************************************************************
 ** Course number line: where you sit between elite and the field
*****************************************************************************/

export const CourseScale: React.FC<{ course: CourseRow }> = ({ course }) => {
    const points = [
        { label: 'Elite', value: course.elite_score, color: DS.gold },
        { label: 'Field', value: course.field_score, color: DS.textMuted },
        { label: 'You', value: course.my_score, color: DS.brand },
    ];
    const min = Math.min(...points.map(p => p.value), 0) - 1;
    const max = Math.max(...points.map(p => p.value), 0) + 1;
    const pos = (v: number) => `${((v - min) / (max - min)) * 100}%`;

    return (
        <Box sx={{ position: 'relative', height: 44, mt: 1 }}>
            {/* Track + par marker */}
            <Box sx={{ position: 'absolute', left: 0, right: 0, top: 21, height: 2, borderRadius: 1, background: DS.inset }} />
            <Box sx={{ position: 'absolute', left: pos(0), top: 15, width: '1px', height: 14, background: DS.borderStrong }} />
            <Typography sx={{ position: 'absolute', left: pos(0), top: 30, transform: 'translateX(-50%)', fontSize: '10px', color: DS.textFaint }}>par</Typography>

            {points.map(p => (
                <Tooltip key={p.label} arrow title={`${p.label}: ${formatToPar(p.value)} to par`}>
                    <Box sx={{ position: 'absolute', left: pos(p.value), top: 0, transform: 'translateX(-50%)', textAlign: 'center', zIndex: p.label === 'You' ? 2 : 1 }}>
                        <Typography sx={{ fontSize: '10px', fontWeight: 700, color: p.color, lineHeight: 1, mb: '3px', whiteSpace: 'nowrap' }}>
                            {p.label}
                        </Typography>
                        <Box sx={{
                            mx: 'auto',
                            width: p.label === 'You' ? 12 : 9,
                            height: p.label === 'You' ? 12 : 9,
                            borderRadius: '50%',
                            background: p.color,
                            border: `2px solid ${DS.surface}`,
                        }} />
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
};

/*****************************************************************************
 ** Hole detail dialog
*****************************************************************************/

export const HoleDialog: React.FC<{ hole: HoleRow | null; onClose: () => void }> = ({ hole, onClose }) => {
    if (!hole) return null;

    const counts = countBy(hole.scores);
    const scores = Object.keys(counts).map(Number).sort((a, b) => a - b);
    const maxCount = Math.max(...Object.values(counts), 1);

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 250 }}
            PaperProps={{
                sx: {
                    background: DS.surface,
                    backgroundImage: 'none',
                    border: `1px solid ${DS.border}`,
                    borderRadius: DS.radius,
                    m: { xs: 1.5, sm: 4 },
                    width: { xs: 'calc(100% - 24px)', sm: undefined },
                },
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 2.5, borderBottom: `1px solid ${DS.border}`, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                        <Overline>{hole.course}</Overline>
                        <Typography sx={{ fontSize: '22px', fontWeight: 800, color: DS.text, letterSpacing: '-0.02em' }}>
                            Hole {hole.holeNum || hole.fullName}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: DS.textMuted, ...numericSx }}>
                            Par {hole.par} · Elite {hole.elite} · played {hole.timesPlayed} times
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} aria-label="Close" sx={{ color: DS.textMuted, alignSelf: 'flex-start', '&:hover': { color: DS.text } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1.5 }}>
                        {[
                            { label: 'Your avg', value: hole.myAvg, color: hole.myAvg > hole.par ? DS.loss : DS.text },
                            { label: 'Field avg', value: hole.field_avg, color: DS.text },
                            { label: 'Vs field', value: formatSigned(hole.vsField, 2), color: trendColor(hole.vsField) },
                            { label: 'Best score', value: hole.bestScore, color: hole.bestScore === 1 ? DS.gold : DS.text },
                        ].map(t => (
                            <Box key={t.label} sx={{ p: 1.75, borderRadius: '12px', background: DS.inset, border: `1px solid ${DS.border}` }}>
                                <Overline>{t.label}</Overline>
                                <Typography sx={{ fontSize: '22px', fontWeight: 800, color: t.color, mt: 0.25, ...numericSx }}>{t.value}</Typography>
                            </Box>
                        ))}
                    </Box>

                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DS.text, mt: 3, mb: 1.5 }}>Your scores on this hole</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {scores.map(score => {
                            const color = BUCKET_COLORS[bucketOf(score, hole.par)];
                            const count = counts[score];
                            return (
                                <Box key={score} sx={{ display: 'grid', gridTemplateColumns: '40px 1fr 64px', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{
                                        height: 28, borderRadius: '6px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 800, fontSize: '13px', color: DS.text, background: alpha(color, 0.35),
                                    }}>
                                        {score}
                                    </Box>
                                    <Box sx={{ height: 10, borderRadius: '999px', background: DS.inset, overflow: 'hidden' }}>
                                        <Box sx={{ height: '100%', width: `${(count / maxCount) * 100}%`, background: color, borderRadius: '999px' }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '12px', color: DS.textMuted, textAlign: 'right', ...numericSx }}>
                                        {count}× · {Math.round((count / hole.timesPlayed) * 100)}%
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                    <Typography sx={{ fontSize: '12px', color: DS.textMuted, mt: 2 }}>
                        This hole plays {hole.fieldToPar > 0 ? `${hole.fieldToPar} over` : hole.fieldToPar < 0 ? `${Math.abs(hole.fieldToPar)} under` : 'at'} par for the field.
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
